import { 
  collection, 
  query, 
  where, 
  Timestamp, 
  orderBy, 
  onSnapshot,
  Unsubscribe,
  DocumentData,
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from './firebase';
import { format, eachDayOfInterval } from 'date-fns';

export interface TaxSummary {
  rate: number;
  pretaxAmount: number;
  taxAmount: number;
}

interface PaymentMethodsSummary {
  cash: number;
  card: number;
  ticket: number;
  app: number;
  other: number;
}

export interface AccountingData {
  id: string;
  date: Timestamp;
  totalAmount: number;
  totalBase: number;
  totalQuota: number;
  taxBase: number;
  taxQuota: number;
  paymentMethods: PaymentMethodsSummary;
  ordersCount: number;
  customersCount: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  taxDetails: TaxSummary[];  
}

export class AccountingService {
  private readonly accountingPath: string;
  private readonly foodTruckId: string;
  private readonly TVA_RATE = 0.021; // 2.10%

  constructor(foodTruckId: string) {
    if (!foodTruckId) {
      throw new Error('Food truck ID is required');
    }
    this.foodTruckId = foodTruckId;
    this.accountingPath = `foodTrucks/${foodTruckId}/accounting`;
  }

  /**
   * Récupère les données comptables pour une date spécifique
   * @param date - Date pour laquelle récupérer les données
   */
  async getAccountingDataForDay(date: Date): Promise<AccountingData | null> {
    try {
      const dayId = format(date, 'yyyy-MM-dd');
      const docRef = doc(db, this.accountingPath, dayId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return this.mapAccountingDocData(docSnap.id, docSnap.data());
      } else {
        console.log(`No accounting data found for ${dayId}`);
        return null;
      }
    } catch (error) {
      console.error('Error getting accounting data for day:', error);
      throw error;
    }
  }

  /**
   * Récupère les données comptables pour une période donnée
   * @param startDate - Date de début de la période
   * @param endDate - Date de fin de la période
   */
  async getAccountingData(startDate: Date, endDate: Date): Promise<AccountingData[]> {
    try {
      // Créez un tableau de toutes les dates dans l'intervalle
      const dates = eachDayOfInterval({ start: startDate, end: endDate });
      
      // Générez tous les IDs de document à récupérer
      const docIds = dates.map(date => format(date, 'yyyy-MM-dd'));
      
      // Préparez un tableau pour stocker les résultats
      const results: AccountingData[] = [];
      
      // Récupérez chaque document individuellement
      for (const dayId of docIds) {
        const docRef = doc(db, this.accountingPath, dayId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          results.push(this.mapAccountingDocData(docSnap.id, docSnap.data()));
        }
      }
      
      // Triez les résultats par date
      return results.sort((a, b) => a.date.seconds - b.date.seconds);
    } catch (error) {
      console.error('Error getting accounting data for period:', error);
      throw error;
    }
  }

  /**
   * Met à jour les données en temps réel pour une date spécifique
   * @param onUpdate - Callback appelé lors des mises à jour
   * @param onError - Callback appelé en cas d'erreur
   * @param date - Date pour laquelle récupérer les données comptables
   */
  listenToAccountingData(
    onUpdate: (data: AccountingData[]) => void,
    onError: (error: Error) => void,
    date: Date = new Date()
  ): Unsubscribe {
    try {
      const dayId = format(date, 'yyyy-MM-dd');
      const docRef = doc(db, this.accountingPath, dayId);
      
      return onSnapshot(
        docRef,
        (docSnap) => {
          if (docSnap.exists()) {
            const accountingData = this.mapAccountingDocData(docSnap.id, docSnap.data());
            onUpdate([accountingData]);
          } else {
            // Si le document n'existe pas encore, retourner un tableau vide
            onUpdate([]);
          }
        },
        onError
      );
    } catch (error) {
      console.error('Error setting up accounting listener:', error);
      throw error;
    }
  }

  /**
   * Obtient les données comptables pour une plage de dates en temps réel
   * @param onUpdate - Callback appelé lors des mises à jour
   * @param onError - Callback appelé en cas d'erreur
   * @param startDate - Date de début de la période
   * @param endDate - Date de fin de la période
   */
  listenToAccountingPeriod(
    onUpdate: (data: AccountingData[]) => void,
    onError: (error: Error) => void,
    startDate: Date,
    endDate: Date
  ): Unsubscribe {
    try {
      const startTimestamp = Timestamp.fromDate(startDate);
      const endTimestamp = Timestamp.fromDate(endDate);
      
      const accountingQuery = query(
        collection(db, this.accountingPath),
        where('date', '>=', startTimestamp),
        where('date', '<=', endTimestamp),
        orderBy('date', 'asc')
      );
      
      return onSnapshot(
        accountingQuery,
        (snapshot) => {
          const accountingData = snapshot.docs.map(doc => 
            this.mapAccountingDocData(doc.id, doc.data())
          );
          onUpdate(accountingData);
        },
        onError
      );
    } catch (error) {
      console.error('Error setting up accounting period listener:', error);
      throw error;
    }
  }

  /**
   * Mappe les données Firestore en objets AccountingData
   * @private
   * @param id - ID du document
   * @param data - Données du document
   * @returns Données comptables formatées
   */
  private mapAccountingDocData(id: string, data: DocumentData): AccountingData {
    return {
      id,
      date: data.date,
      totalAmount: Number(data.totalAmount) || 0,
      totalBase: Number(data.totalBase) || 0,
      totalQuota: Number(data.totalQuota) || 0,
      taxBase: Number(data.taxBase) || 0,
      taxQuota: Number(data.taxQuota) || 0,
      paymentMethods: {
        cash: Number(data.paymentMethods?.cash) || 0,
        card: Number(data.paymentMethods?.card) || 0,
        ticket: Number(data.paymentMethods?.ticket) || 0,
        app: Number(data.paymentMethods?.app) || 0,
        other: Number(data.paymentMethods?.other) || 0
      },
      ordersCount: Number(data.ordersCount) || 0,
      customersCount: Number(data.customersCount) || 0,
      createdAt: data.createdAt || Timestamp.now(),
      updatedAt: data.updatedAt || Timestamp.now(),
      taxDetails: Array.isArray(data.taxDetails) 
        ? data.taxDetails.map((detail: any) => ({
            rate: Number(detail.rate) || 0,
            pretaxAmount: Number(detail.pretaxAmount) || 0,
            taxAmount: Number(detail.taxAmount) || 0
          }))
        : [],
    };
  }
}

// Export singleton instance factory
export const accountingService = {
  getInstance(foodTruckId: string): AccountingService {
    if (!foodTruckId) {
      throw new Error('Food truck ID is required');
    }
    return new AccountingService(foodTruckId);
  }
};